import { faro } from '@grafana/faro-react';

export const setFaroIsGuestAttribute = (isGuest) => {
  if (!faro?.api?.setUser) {
    return;
  }

  faro.api.setUser({
    attributes: {
      isGuest: String(isGuest),
    },
  });
};

export const logRedirectSuccess = (redirectUrl, additionalData = {}) => {
  faro.api.pushLog(['Redirect successful'], {
    level: 'info',
    context: {
      redirectUrl,
      ...additionalData,
    },
  });
};

export const logRedirectError = (error, redirectUrl, additionalData = {}) => {
  faro.api.pushLog(['Redirect failed'], {
    level: 'error',
    context: {
      error,
      redirectUrl,
      ...additionalData,
    },
  });
};

// Pre-authorization specific logging functions
export const logPreAuthSkipped = (reason, paymentIntentId, selectedPayment) => {
  if (faro?.api?.pushLog) {
    faro.api.pushLog(['Pre-authorization skipped'], {
      level: 'warn',
      context: {
        reason,
        paymentIntentId,
        selectedPayment,
      },
    });
  }
};

export const logPreAuthCalled = (paymentIntentId, selectedPayment) => {
  if (faro?.api?.pushLog) {
    faro.api.pushLog(['Pre-authorization called'], {
      level: 'info',
      context: {
        paymentIntentId,
        selectedPayment,
      },
    });
  }
};

export const logPreAuthError = (error, paymentIntentId, selectedPayment) => {
  if (faro?.api?.pushLog) {
    faro.api.pushLog(['Pre-authorization error'], {
      level: 'error',
      context: {
        error: error.message || error,
        paymentIntentId,
        selectedPayment,
      },
    });
  }
};

const AMOUNT_ERROR_CODES = ['amount_too_large', 'amount_too_small'];

export const parseStripeAmountErrorCode = (text) => {
  if (!text || typeof text !== 'string') {
    return undefined;
  }
  return AMOUNT_ERROR_CODES.find((code) => text.includes(code));
};

export const logInitialisePaymentError = (error) => {
  if (!faro?.api?.pushLog) {
    return;
  }

  const graphQLErrors = error?.graphQLErrors || [];
  const graphQLMessages = graphQLErrors
    .map((err) => err.message)
    .filter(Boolean);
  const errorCodes = graphQLErrors
    .map((err) => err.extensions?.errorCode)
    .filter(Boolean);
  const combinedText = [error?.message, ...graphQLMessages]
    .filter(Boolean)
    .join(' ');
  const stripeCode = parseStripeAmountErrorCode(combinedText);

  faro.api.pushLog(['Initialise payment error'], {
    level: 'error',
    context: {
      error: error?.message || String(error),
      graphQLMessages: graphQLMessages.join(' | ') || undefined,
      errorCodes: errorCodes.join(',') || undefined,
      stripeCode,
    },
  });
};

export const logStripePaymentError = ({
  code,
  type,
  message,
  paymentIntentId,
  selectedPayment,
  stage,
} = {}) => {
  if (!faro?.api?.pushLog) {
    return;
  }

  const stripeCode = AMOUNT_ERROR_CODES.includes(code)
    ? code
    : parseStripeAmountErrorCode(message);

  faro.api.pushLog(['Stripe payment error'], {
    level: 'error',
    context: {
      code,
      type,
      message,
      paymentIntentId,
      selectedPayment,
      stage,
      stripeCode,
    },
  });
};

export const logOrderStatusSuccess = (attemptCount, additionalData = {}) => {
  faro.api.pushLog(['Order status check succeeded'], {
    level: 'info',
    context: {
      attemptCount,
      message: `Order status became SUCCESSFUL after ${attemptCount} attempt(s)`,
      ...additionalData,
    },
  });
};

export const logOrderStatusMaxAttemptsReached = (
  attemptCount,
  additionalData = {},
) => {
  faro.api.pushLog(['Order status check failed - max attempts reached'], {
    level: 'error',
    context: {
      attemptCount,
      message: `Max attempts (${attemptCount}) reached without success`,
      errorType: 'MAX_ATTEMPTS_REACHED',
      ...additionalData,
    },
  });
};

export const logOrderStatusGraphQLError = (
  error,
  attemptCount,
  additionalData = {},
) => {
  faro.api.pushLog(['Order status check failed - GraphQL/network error'], {
    level: 'error',
    context: {
      attemptCount,
      error: error.message || String(error),
      errorType: 'GRAPHQL_OR_NETWORK_ERROR',
      ...additionalData,
    },
  });
};

let paymentBounceBackLoggedThisLoad = false;

export const logPaymentBounceBack = (reason, additionalData = {}) => {
  paymentBounceBackLoggedThisLoad = true;
  if (faro?.api?.pushLog) {
    faro.api.pushLog(['Payment bounce back'], {
      level: 'warn',
      context: {
        redirectUrl: 'stripe_redirect_back',
        reason,
        flow: 'payment_redirect',
        ...additionalData,
      },
    });
  }
};

export const wasPaymentBounceBackLoggedAndClear = () => {
  const wasLogged = paymentBounceBackLoggedThisLoad;
  paymentBounceBackLoggedThisLoad = false;
  return wasLogged;
};
